import { useFormContext, FormItem } from '@magicdoorinc/form';
import { useLocalization, LabeledSelect, LabeledTextArea, LabeledTextInput, Checkbox, DropdownMenu } from '@magicdoorinc/ui';
import { createMemo, createSignal, onMount, Show, createEffect, on } from 'solid-js';
import { LabeledChartOfAccountSelect } from '~/components/chart-of-accounts/ChartOfAccountSelect';
import { LabelWithOptional } from '~/components/common';
import ExpandableSection from '~/components/common/expandables/ExpandableSection';
import { useCurrentCompany } from '~/contexts/global';
import { mode, PaymentMode } from '~/hooks/usePaymentMode';
import { bankAccountRepository } from '~/repositories/bankAccountRepository';
import { BankAccountType, MagicDoorFeature } from '~/swagger/portal/Api';
import { getOrdinalDay } from '~/utils/date';
import { bankAccountNumber, bankAccountRoutingNumber, bankAccountType } from '~/utils/formRules';
import type { BankAccountSweepLimitsDto, HydratedBankAccountDto } from '~/swagger/portal/Api';

interface BankAccountFormFieldsProps {
  isEdit?: boolean;
  bankAccount?: HydratedBankAccountDto;
  hideCompanyAccountField?: boolean;
}

export function BankAccountFormFields(props: BankAccountFormFieldsProps) {
  const { t } = useLocalization();
  const form = useFormContext();
  const { checkFeature } = useCurrentCompany();

  const getSweepsLimits = async () => {
    return await bankAccountRepository.getSweepsLimits();
  };

  const [sweepsLimits, setSweepsLimits] = createSignal<BankAccountSweepLimitsDto>({
    interval: 1,
  });

  const [expandableOpen, setExpandableOpen] = createSignal(false);
  const [useSameChartOfAccounts, setUseSameChartOfAccounts] = createSignal(true);

  const isPayMode = createMemo(() => mode() === PaymentMode.Pay);

  const options = createMemo(() => {
    let options: any[] = [];
    for (let i = 1; i <= 31; i++) {
      options.push({
        label: t('{day} of every month', {
          day: getOrdinalDay(i),
        }),
        value: i,
        disabled: false,
        disabledReason: t('For your current subscription plan, sweeps can only be made every {num} days.', {
          num: sweepsLimits().interval,
        }),
      });
    }
    const scheduledSweepDays = form.formStore.scheduledSweepDays;

    if (scheduledSweepDays?.length) {
      options = options.map((option) => {
        if (scheduledSweepDays.includes(option.value)) {
          return {
            ...option,
            disabled: false,
          };
        }
        let shouldDisable = false;
        scheduledSweepDays.forEach((selectedValue: any) => {
          if (option.value !== selectedValue && Math.abs(selectedValue - option.value) < sweepsLimits().interval) {
            shouldDisable = true;
          }
        });
        return {
          ...option,
          disabled: shouldDisable,
        };
      });
    }
    return options;
  });

  const showAccountNumberField = createMemo(() => {
    return !props.isEdit || !props.bankAccount?.accountNumber;
  });

  const showRoutingNumberField = createMemo(() => {
    return !props.isEdit || !props.bankAccount?.routingNumber;
  });

  const showTypeField = createMemo(() => {
    return !props.isEdit || !props.bankAccount?.type;
  });

  onMount(async () => {
    const res = await getSweepsLimits();
    setSweepsLimits(res);
  });

  createEffect(() => {
    if (!form.isValidate) {
      setExpandableOpen(true);
    }
  });

  const showCompanyAccountField = createMemo(() => {
    if (props.hideCompanyAccountField) {
      return false;
    }
    return checkFeature(MagicDoorFeature.CompanyLedger);
  });

  const isCompanyAccount = createMemo(() => {
    return !!form.formStore.companyAccount;
  });

  const accountNumberRules = createMemo(() => bankAccountNumber(t, isCompanyAccount()));
  const routingNumberRules = createMemo(() => bankAccountRoutingNumber(t, isCompanyAccount()));
  const accountTypeRules = createMemo(() => bankAccountType(t, isCompanyAccount()));

  createEffect(
    on(
      isCompanyAccount,
      (isCompany) => {
        if (isCompany) {
          form.clearValidate(['accountNumber']);
          form.clearValidate(['routingNumber']);
          form.clearValidate(['type']);
        } else {
          form.validateForm();
        }
      },
      { defer: true }
    )
  );

  return (
    <>
      <FormItem
        formFieldName="name"
        label={t('Name')}
        placeholder={t('Please enter the name')}
        component={LabeledTextInput}
        rules={[
          {
            required: true,
            message: t('Please enter the name'),
          },
          { length: 100, message: t('{name} must be {length} characters or less', { name: t('Name'), length: '100' }) },
        ]}
      />
      <Show when={showAccountNumberField()}>
        <FormItem
          formFieldName="accountNumber"
          placeholder={t('Please enter')}
          label={t('Account number')}
          rules={accountNumberRules()}
          component={LabeledTextInput}
        />
      </Show>
      <Show when={showRoutingNumberField()}>
        <FormItem
          formFieldName="routingNumber"
          placeholder={t('Please enter')}
          label={t('Routing number')}
          component={LabeledTextInput}
          rules={routingNumberRules()}
        />
      </Show>
      <Show when={showTypeField()}>
        <FormItem
          formFieldName="type"
          placeholder={t('Please enter')}
          label={t('Type')}
          component={DropdownMenu}
          rules={accountTypeRules()}
          options={Object.entries(BankAccountType).map(([key, value]) => ({ label: t(key), value }))}
          onChangeMethodName="onChange"
        />
      </Show>

      <ExpandableSection
        open={expandableOpen()}
        onOpenChange={setExpandableOpen}
        headerBorder
        class="rounded-lg border border-separator px-3 text-base"
        triggerTitle={<LabelWithOptional label={t('Advanced information')} />}>
        <div class="grid grid-cols-1 gap-x-4 gap-y-5 pb-3">
          <Show when={!props.isEdit}>
            <div class="col-span-full flex items-center gap-2">
              <Checkbox
                checked={useSameChartOfAccounts()}
                onInput={(checked: boolean) => {
                  setUseSameChartOfAccounts(checked);
                  if (checked) {
                    form.setFieldsValue({ chartOfAccountIds: undefined }, false);
                  }
                }}
              />
              <span class="text-sm text-text-02">{t('Use same chart of accounts')}</span>
            </div>
          </Show>
          <Show when={props.isEdit || !useSameChartOfAccounts()}>
            <FormItem
              formFieldName="chartOfAccountIds"
              label={t('Chart of accounts')}
              component={LabeledChartOfAccountSelect}
              placeholder={t('Please select chart of account')}
              class="col-span-full"
              multiple
              types="asset"
            />
          </Show>
          <FormItem
            formFieldName="description"
            placeholder={t('Please enter')}
            label={t('Description')}
            rows={5}
            maxlength={500}
            component={LabeledTextArea}
            class="col-span-full"
          />
          <Show when={!isPayMode()}>
            <div class="col-span-full normal-case">
              <FormItem
                description={t(
                  'On the withdrawal date, the funds from the third-party platform will be automatically withdrawn to the Funding account.'
                )}
                formFieldName="scheduledSweepDays"
                placeholder={t('Please enter')}
                label={t('Withdrawal date')}
                options={options()}
                component={LabeledSelect}
                multiple
              />
              <div class="mt-1.5 text-xs text-text-03">
                {t(
                  'If the set date exceeds the number of days in a certain month, the payment will be made on the last day of that month.'
                )}
              </div>
            </div>
          </Show>
          <Show when={showCompanyAccountField() && !props.isEdit}>
            <FormItem
              formFieldName="companyAccount"
              label={t('Company account')}
              component={Checkbox}
              class="col-span-full"
              description={t('Enable if this bank account is owned by the company')}
              valuePropName="checked"
              showLabel
              disabled={props.isEdit}
            />
            <div class="col-span-full rounded-lg border border-[#E5E6F8] bg-[#F5F6FE] px-4 py-3 text-xs leading-relaxed text-text-02">
              <p>
                <span class="font-medium text-text-01">{t('Property bank account')}</span>
                {' — '}
                {t('for receiving rents, paying property-related expenses, and distributing owner payments.')}
              </p>
              <p class="mt-1">
                <span class="font-medium text-text-01">{t('Company bank account')}</span>
                {' — '}
                {t('for non-property transactions belonging to the property management company itself.')}
              </p>
            </div>
          </Show>
        </div>
      </ExpandableSection>
    </>
  );
}
